//@ts-check
import * as THREE from "three";
import viewerMgrInstance from '../ViewerMgr.js';

/**
 * @module ColorBy
 * @description This module provides functions for elements' materials and custom and default materials' color properties 
 */

/**
 * Applies custom color to an individual element
 * @param {string} element_ID 
 * @param {Number} color hex Color
 * @param {boolean} [render = false] render or not ? 
 * @returns Return success or error messages 
 */
export function colorElement(element_ID, color, render = false) {
  var scope = this;
  try {
    if (scope.modelScene.children.length != 0) {

      let child = scope.elementsMap.get(element_ID);

      // capture original once
      if (!child.userData.originalMaterial) {
        child.userData.originalMaterial = child.material;
      }
      
      if (child.userData.colorMaterial) {
        if (child.userData.colorMaterial.userData.opacity) {

          if (scope.modelMap.keys().includes(child.name)) {
            child.material.transparent = false;
            child.material.opacity = scope.modelMap.get(child.name).material.opacity;
          }
        }
      }

      //Create new material to force the material to update its color
      var colorTempMaterial = new THREE.MeshBasicMaterial({
        transparent: true,
        forceSinglePass: true,
        side: THREE.DoubleSide,
        opacity: 1.0,
        reflectivity: 0.0,
        clippingPlanes: child.material.clippingPlanes,
        clipShadows: child.material.clipShadows,
        clipIntersection: child.material.clipIntersection,
      });
      colorTempMaterial.color.setHex(color)
      child.userData.colorMaterial = colorTempMaterial;
      
      child.material = colorTempMaterial;

      if (!scope.showEdges) scope.scene.add(scope.edgesMap.get(child.name));

      if (render) { viewerMgrInstance.render(); }
      var returnValue = {
        "status": "success",
        "message": "Color element API"
      }
      return returnValue;
    }
    else {
      var returnValue = {
        "status": "failed",
        "message": "ERROR : Model is not loaded in viewer."
      }
      return returnValue;
    }
  }
  catch (err) {
    var returnValue = {
      "status": "failed",
      "message": 'System Error: ' + err.message
    }
    return returnValue;
  }
}

/**
 * Resets color to default color of an individual element
 * @param {string} element_ID 
 * @returns Return success or error messages 
 */
export function resetColor(element_ID) {
  var scope = this;
  try {
    if (scope.modelScene.children.length != 0) {
      let child = scope.elementsMap.get(element_ID);
      if (child instanceof THREE.Mesh) scope.resetElement(element_ID);
      var returnValue = {
        "status": "success",
        "message": "Reset color API"
      }
      return returnValue;
    }
    else {
      var returnValue = {
        "status": "failed",
        "message": "ERROR : Model is not loaded in viewer."
      }
      return returnValue;
    }
  }
  catch (err) {
    var returnValue = {
      "status": "failed",
      "message": "Sytem Error" + err.message
    }
    return returnValue;
  }
}

// reset color of all elements
/**
 * Resets default colors of all the elements in the scene
 * @returns Return success or error messages  
 */
export function resetAllColors() {
  var scope = this;
  try {
    if (scope.modelScene.children.length != 0) {
      scope.elementsMap.forEach((element, name) => {
        if (element instanceof THREE.Mesh) {
          scope.resetElement(name);
          if (!scope.showEdges) scope.scene.remove(scope.edgesMap.get(name));
        }
      })
      this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
      var returnValue = {
        "status": "success",
        "message": "Reset all colors API"
      }
      return returnValue;
    }
    else {
      var returnValue = {
        "status": "failed",
        "message": "ERROR : Model is not loaded in viewer."
      }
      return returnValue;
    }
  }
  catch (err) {
    var returnValue = {
      "status": "failed",
      "message": "Stytem error message " + err.message
    }
    return returnValue;
  }
}

// set colors to multiple elements
/**
 * Color by elements
 * @param {Array<string>} element_Ids  Array of element ids
 * @param {Array<Number>} colors Array of hex colors
 * @param {boolean} hide hide elements or not
 * @param {boolean} [showAll = false] 
 * @returns {Object} Return success or error messages  
 * { status:  mesage: }
 */
export function colorElements(element_Ids, colors, hide, showAll = false) {
  var scope = this;
  try {
    if (scope.modelScene.children.length != 0) {
      //set color for all provided elements
      for (let i = 0; i < element_Ids.length; i++) {
        scope.colorElement(element_Ids[i], colors[i]);
      }

      //reset
      let elementIdsSet = new Set(element_Ids);
      this.elementsMap.forEach((element, name) => {
        if (!elementIdsSet.has(name)) {
          if (hide) {
            this.resetElement(name);
            this.showElement(name, false);
          } else {
            if (showAll && !element.visible) {
              element.visible = true;
            }
          }
        }
      });
      this.renderer.toneMapping = THREE.LinearToneMapping;

      var returnValue = {
        "status": "success",
        "message": "Color elements API"
      }
      return returnValue;
    }
    else {
      var returnValue = {
        "status": "failed",
        "message": "ERROR : Model is not loaded in viewer."
      }
      return returnValue;
    }
  }
  catch (err) {
    var returnValue = {
      "status": "failed",
      "message": "System Error:" + err.message
    }
    return returnValue;
  }
}



/**
 * Resets the color overrides of a single element
 * @param {string} element_ID unique element id 
 * @param {boolean} [render = false] render the scene ?
 * @returns {Object} Return success or error messages  
 * { status:  mesage: }
 */
export function resetElement(element_ID, render = false) {
  const scope = this;
  try {
    const child = scope.elementsMap.get(element_ID);
    // Determine material to restore: colorMaterial takes precedence
    let restoreMat;
    if (child.userData.colorMaterial) {
      restoreMat = child.userData.colorMaterial;
    } else if (child.userData.originalMaterial) {
      restoreMat = child.userData.originalMaterial;
    } else {
      // fallback to original model material
      restoreMat = scope.modelMap.get(child.name).material;
    }
    
    // Preserve clipping settings
    const old = child.material;
    restoreMat.clippingPlanes = old.clippingPlanes;
    restoreMat.clipShadows = old.clipShadows;
    restoreMat.clipIntersection = old.clipIntersection;

    child.material = restoreMat;
    // Ensure restored material is fully opaque
    child.material.opacity = 1.0;
    child.material.transparent = false;
    // Clear filter flags
    delete child.userData.ghosted;
    delete child.userData.opacity;

    if (render) {
      viewerMgrInstance.render();
    }
    return { status: 'success', message: 'Reset element API' };
  } catch (err) {
    return { status: 'failed', message: 'System Error: ' + err.message };
  }
}

/**
 * Clears all colorBy material overrides, allowing resetElement to revert to original materials
 */
export function clearColorBy() {
  const scope = this;
  try {
    scope.elementsMap.forEach((child, name) => {
      // Remove color override
      delete child.userData.colorMaterial;
      // Remove filter flags
      delete child.userData.ghosted;
      delete child.userData.opacity;
      // Reset material to original model material
      const model = scope.modelMap.get(child.name);
      if (model && model.material) {
        child.material = model.material;
      }
    });
    return { status: 'success', message: 'Cleared colorBy and restored materials' };
  } catch (err) {
    return { status: 'failed', message: err.message };
  }
}

/**
 * Removes filter effects and restores color overrides or original materials
 * @returns {Object} Status message
 */
export function removeFilterEffects() {
  const scope = this;
  scope.elementsMap.forEach((child) => {
    // restore color override if present, else original
    if (child.userData.colorMaterial) {
      child.material = child.userData.colorMaterial;
    } else if (child.userData.originalMaterial) {
      child.material = child.userData.originalMaterial;
    }
    // clear any filter flags and special materials
    delete child.userData.ghosted;
    delete child.userData.opacity;
    delete child.userData.filtered;
    delete child.userData.fadedMaterial;
  });
  return { status: 'success', message: 'Removed filter effects' };
}

/**
 * Sets global selection (pick) Color : scope.pickColor
 * @param {Number} selectionColor 
 */
export function setSelectionColor(selectionColor) {
  var scope = this;
  scope.pickColor = this.parseColor(selectionColor);
  scope.selectionMaterial.color = scope.pickColor;
}

/**
 * Sets global highlight color : scope.highlightColor
 * @param {Number} highlightColor hex color
 */
export function setHighlightColor(highlightColor) {
  var scope = this;
  scope.highlightColor = this.parseColor(highlightColor);
  scope.highlightMaterial.color = scope.highlightColor;
}

// set scene background color
/**
 * Sets global background color 
 * @param {Number} backgroundColor  hex color
 * @returns {Object} Return success or error messages  
 * { status:  mesage: }
 */
export function setSceneBackground(backgroundColor) {
  var scope = this;
  try {
    if (scope.scene != undefined || scope.scene != null) {
      scope.backgroundColor = backgroundColor;
      scope.scene.background = this.parseColor(backgroundColor);

      var returnValue = {
        status: "success",
        message: "Scene background color updated.",
      };
      return returnValue;
    } else {
      var returnValue = {
        status: "failed",
        message: "ERROR : Scene is undefined or null.",
      };
      return returnValue;
    }
  } catch (err) {
    var returnValue = {
      status: "failed",
      message: "System Error: " + err.message,
    };
    return returnValue;
  }
}

